/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import FileUpload from "components/FileUpload";
import ImageModal from "components/ImageModal";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  editBailRight,
  editBailRight2,
  fetchBailHearingDecisionByBailRightId,
  fetchBailRightById,
} from "services/bailRightService";
import {
  BailHearingDecision,
  BailRight,
  BailRightStatus,
  BailRightType,
  Roles,
  Suspect,
} from "types";
import { BASE_URL } from "utils/constant";
import { DateView, errorHandlerOnForm, hasRole } from "utils/helper";
import BailRightFeedbackSection from "./fragment/BailRightFeedback";

type BailRightDetailOnlyViewProps = {
  bailRightId: string | number;
};

// Enhanced Status Badge Component
const StatusBadge: React.FC<{ status: string; t: (key: string) => string }> = ({
  status,
  t,
}) => {
  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: string } = {
      submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      assigned_to_prosecutor: "bg-red-100 text-red-800 border-red-200",
      forwarded_to_coordinator:
        "bg-purple-100 text-purple-800 border-purple-200",
      bail_granted: "bg-green-100 text-green-800 border-green-200",
      bail_rejected: "bg-red-100 text-red-800 border-red-200",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
        status,
      )}`}
    >
      {BailRightStatus[status as keyof typeof BailRightStatus] || status}
    </span>
  );
};

// Reusable Card Component
const InfoCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  title?: string;
}> = ({ children, className = "", title }) => (
  <div
    className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}
  >
    {title && (
      <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-3">
        {title}
      </h3>
    )}
    {children}
  </div>
);

// Info Row Component
const InfoRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className = "" }) => (
  <div
    className={`flex justify-between items-start py-3 border-b border-gray-100 last:border-b-0 ${className}`}
  >
    <dt className="text-sm font-medium text-gray-600 flex-shrink-0 pr-4">
      {label}
    </dt>
    <dd className="text-sm text-gray-900 text-right font-medium">{value}</dd>
  </div>
);

// Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "danger" | "outline";
  loading?: boolean;
  className?: string;
  t: (key: string) => string;
}> = ({
  onClick,
  children,
  variant = "primary",
  loading = false,
  className = "",
  t,
}) => {
  const baseClasses =
    "px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    danger: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${
        loading ? "opacity-50 cursor-not-allowed" : ""
      } ${className}`}
      onClick={onClick}
      disabled={loading}
    >
      {loading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          {t("processing")}
        </>
      ) : (
        children
      )}
    </button>
  );
};

// File Download Component
const FileDownload: React.FC<{
  name: string;
  url?: string;
  t: (key: string) => string;
}> = ({ name, url, t }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
        <svg
          className="w-5 h-5 text-red-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <span className="font-medium text-gray-900">{name}</span>
    </div>
    {url ? (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
      >
        {t("download")}
      </a>
    ) : (
      <span className="text-gray-400 text-sm">{t("not_available")}</span>
    )}
  </div>
);

// Suspect Card Component
const SuspectCard: React.FC<{
  suspect: any;
  onViewDetails: () => void;
  t: (key: string) => string;
}> = ({ suspect, onViewDetails, t }) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
    <div className="flex items-center gap-3">
      <img
        src={suspect?.picture_file || require("../../images/imageVector.png")}
        alt={t("suspect")}
        className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300"
      />
      <div>
        <h4 className="font-semibold text-gray-900">
          {`${suspect?.first_name} ${suspect?.middle_name} ${suspect?.last_name}`}
        </h4>
        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
          {suspect?.age && (
            <span>
              {suspect.age} {t("years")}
            </span>
          )}
          {suspect?.education_level && (
            <>
              <span>•</span>
              <span>{suspect.education_level}</span>
            </>
          )}
          {suspect?.nationality && (
            <>
              <span>•</span>
              <span>{suspect.nationality}</span>
            </>
          )}
        </div>
      </div>
    </div>
    <button
      onClick={onViewDetails}
      className="p-2 text-gray-600 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
      title={t("view_suspect_details")}
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    </button>
  </div>
);

const BailRightDetailOnlyView: React.FC<BailRightDetailOnlyViewProps> = ({
  bailRightId,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [loading, setLoading] = React.useState(false);
  const [prosecutors, setProsecutors] = React.useState<
    {
      user: { id: string; first_name: string; last_name: string };
    }[]
  >([]);

  const [actionIsLoading, setActionIsLoading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isSuspectDetailOpen, setIsSuspectDetailOpen] = useState(false);

  const [bailRight, setBailRight] = useState<BailRight>();
  const [bailHearingDecisions, setBailHearsingDecisions] = useState<
    BailHearingDecision[]
  >([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showHearing, setShowHearing] = useState(false);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const [prosecutorSummary, setProsecutorSummary] = useState("");
  const [prosecutorDcument, setProsecutorDocument] = useState<File | null>(
    null,
  );
  const [referenceNumber, setReferenceNumber] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentReason, setAppointmentReason] = useState("");
  const [courtHearing, setCourtHearing] = useState("");
  const [courtOrder, setCourtOrder] = useState("");
  const [dailyHearingDetail, setDailyHearingDetail] = useState("");
  const [isFinalDecision, setIsFinalDecision] = useState(false);
  const [finalOutcome, setFinalOutcome] = useState<"approved" | "rejected">();
  const [prosecutorFeedback, setProsecutorFeedback] = useState("");
  const [toAppeal, setToAppeal] = useState(false);
  const [receiptOfPayment, setRecipetOfPayment] = useState<File | null>(null);
  const [feedback, setFeedback] = useState("");
  const [assignProsecutorUserid, setAssignProsecutorUserid] = useState<
    string | null
  >(null);

  const fetchData = () => {
    if (!bailRightId) return;

    fetchBailRightById(bailRightId + "")
      .then((data) => {
        setBailRight(data);
        if (data?.status === "submitted") setShowFeedback(true);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      });
  };

  const fetcHearingData = () => {
    if (bailRight?.uu_id === undefined) return;

    fetchBailHearingDecisionByBailRightId(bailRight?.uu_id)
      .then((data) => {
        setBailHearsingDecisions(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      });
  };

  const fetcProsecutorData = (
    adminId: string | number,
    orgId: string | number,
  ) => {
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${adminId}&org_id=${orgId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        setProsecutors(
          data?.filter(
            (d: any) =>
              d?.roles?.includes("Prosecutor") ||
              d?.roles?.includes("ProsecutionCaseProsecutor"),
          ),
        );
      })
      .catch((error) => {
        console.error(t("error_fetching_prosecutors"), error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [bailRightId]);

  useEffect(() => {
    if (bailRight?.uu_id) {
      fetcHearingData();
    }
  }, [bailRight]);

  useEffect(() => {
    if (loginData && isCoordinator) {
      setLoading(true);
      fetcProsecutorData(loginData?.admin_unit, loginData?.org_unit);
    }
  }, [loginData, isCoordinator]);

  // Render Hearing Decisions
  const renderHearingDecisions = () => {
    if (!bailHearingDecisions?.length) {
      return (
        <div className="text-center py-8 text-gray-500 bg-white rounded-xl border border-gray-200">
          <svg
            className="w-12 h-12 text-gray-300 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-500">{t("no_hearing_decisions_available")}</p>
        </div>
      );
    }

    return (
      <div className="">
        {bailHearingDecisions
          .slice()
          .sort((a, b) => {
            const dateA = a.appointment_date
              ? new Date(a.appointment_date).getTime()
              : 0;
            const dateB = b.appointment_date
              ? new Date(b.appointment_date).getTime()
              : 0;
            return dateB - dateA;
          })
          .map((hearing, index) => (
            <InfoCard
              key={index}
              title={`${t("hearing_decision")} #${index + 1}`}
            >
              <div className="space-y-4">
                {/* Prosecutor Info */}
                <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <span className="text-red-600">👨‍⚖️</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {hearing?.assign_prosecutor}
                    </p>
                    <p className="text-sm text-gray-600">{t("prosecutor")}</p>
                  </div>
                </div>

                {/* Hearing Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      label: t("appointment_date"),
                      value: DateView(hearing?.appointment_date),
                    },
                    {
                      label: t("appointment_reason"),
                      value: hearing?.appointment_reason,
                    },
                    {
                      label: t("decision_action"),
                      value: hearing?.decision_action
                        ? {
                            grant: t("grant"),
                            not_grant: t("not_grant"),
                            pending: t("pending"),
                          }[hearing.decision_action]
                        : t("na"),
                    },
                    {
                      label: t("bail_stance"),
                      value: hearing?.bail_stance
                        ? {
                            favored: t("favored"),
                            opposed: t("opposed"),
                            favored_rejected: t("favored_rejected"),
                          }[hearing.bail_stance]
                        : t("na"),
                    },
                    {
                      label: t("final_outcome"),
                      value: hearing?.final_outcome
                        ? { approved: t("approved"), rejected: t("rejected") }[
                            hearing.final_outcome
                          ]
                        : t("na"),
                    },
                    {
                      label: t("to_appeal"),
                      value:
                        hearing?.to_appeal !== undefined
                          ? hearing.to_appeal
                            ? t("yes")
                            : t("no")
                          : t("na"),
                    },
                  ].map((item, idx) => (
                    <div key={idx}>
                      <label className="text-sm font-medium text-gray-600">
                        {item.label}
                      </label>
                      <p className="text-gray-900 font-medium mt-1">
                        {item.value || t("na")}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Detailed Information */}
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      {t("court_hearing")}
                    </label>
                    <p className="text-gray-900 mt-1 leading-relaxed">
                      {hearing.court_hearing || t("na")}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      {t("court_order")}
                    </label>
                    <p className="text-gray-900 mt-1 leading-relaxed">
                      {hearing.court_order || t("na")}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      {t("prosecutor_feedback")}
                    </label>
                    <p className="text-gray-900 mt-1 leading-relaxed">
                      {hearing.prosecutor_feedback || t("na")}
                    </p>
                  </div>

                  {hearing.daily_hearing_detail && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        {t("daily_hearing_detail")}
                      </label>
                      <p className="text-gray-900 mt-1 leading-relaxed">
                        {hearing.daily_hearing_detail}
                      </p>
                    </div>
                  )}
                </div>

                {/* Receipt of Payment */}
                {hearing?.receipt_of_payment_details && (
                  <FileDownload
                    name={t("receipt_of_payment")}
                    url={hearing.receipt_of_payment_details.url?.toString()}
                    t={t}
                  />
                )}
              </div>
            </InfoCard>
          ))}
      </div>
    );
  };

  // Render Bail Right Information
  const renderBailRightInfo = () => (
    <InfoCard title={t("bail_right_information")}>
      <div className="space-y-6">
        {/* Status and Reference */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <span className="text-sm font-medium text-gray-600">
              {t("reference_number")}
            </span>
            <p className="text-lg font-bold text-gray-900">
              #{bailRight?.crime_registration_number}
            </p>
          </div>
          {bailRight?.status && <StatusBadge status={bailRight.status} t={t} />}
        </div>

        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              label: t("bail_type"),
              value: bailRight?.bail_type
                ? BailRightType[bailRight.bail_type]
                : t("na"),
            },
            {
              label: t("bail_amount"),
              value: bailRight?.bail_amount || t("na"),
            },
            {
              label: t("crime_registration_number"),
              value: bailRight?.crime_registration_number || t("na"),
            },
            {
              label: t("incident_registration_number"),
              value: bailRight?.incident_registration_number || t("na"),
            },
            {
              label: t("prosecutor_registration_number"),
              value: bailRight?.prosecutor_registration_number || t("na"),
            },
            {
              label: t("court_registration_number"),
              value: bailRight?.court_registration_number || t("na"),
            },
            { label: t("court_name"), value: bailRight?.court_name || t("na") },
            {
              label: t("reference_number"),
              value: bailRight?.reference_number || t("na"),
            },
            {
              label: t("court_open_day"),
              value: DateView(bailRight?.court_open_day) || t("na"),
            },
            {
              label: t("court_hearing_day"),
              value: DateView(bailRight?.court_hearing_date) || t("na"),
            },
            {
              label: t("appointment_date"),
              value: DateView(bailRight?.appointment_date) || t("na"),
            },
            {
              label: t("assigned_prosecutor"),
              value: bailRight?.assign_prosecutor || t("na"),
            },
          ].map((item, idx) => (
            <div key={idx}>
              <label className="text-sm font-medium text-gray-600">
                {item.label}
              </label>
              <p className="text-gray-900 font-medium mt-1">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Bail Evidence Description */}
        {bailRight?.bail_evidence_description && (
          <div>
            <label className="text-sm font-medium text-gray-600">
              {t("bail_evidence_description")}
            </label>
            <p className="text-gray-900 mt-1 leading-relaxed">
              {bailRight.bail_evidence_description}
            </p>
          </div>
        )}

        {/* Suspect Information */}
        <div>
          <label className="text-sm font-medium text-gray-600 mb-3 block">
            {t("suspect_information")}
          </label>
          {bailRight?.suspect_fullname && !bailRight?.suspect_info && (
            <p className="text-gray-900">{bailRight.suspect_fullname}</p>
          )}
          {bailRight?.suspect_info && (
            <SuspectCard
              suspect={bailRight.suspect_info}
              onViewDetails={() => setIsSuspectDetailOpen(true)}
              t={t}
            />
          )}
        </div>

        {/* Evidence Files */}
        <div>
          <label className="text-sm font-medium text-gray-600 mb-3 block">
            {t("evidence_files")}
          </label>
          <div className="space-y-3">
            {bailRight?.court_bail_application_file && (
              <FileDownload
                name={t("court_bail_application_file")}
                url={bailRight.court_bail_application_file.toString()}
                t={t}
              />
            )}
          </div>
        </div>
      </div>
    </InfoCard>
  );

  // Render Prosecutor Summary
  const renderProsecutorSummary = () => {
    if (!bailRight?.prosecutor_summary) return null;

    return (
      <InfoCard title={t("prosecutor_summary")}>
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <span className="text-red-600">👨‍⚖️</span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {bailRight?.assign_prosecutor}
              </p>
              <p className="text-sm text-gray-600">{t("prosecutor")}</p>
            </div>
            <div className="ml-auto">
              <StatusBadge
                status={
                  bailRight.bail_type ? BailRightType[bailRight.bail_type] : ""
                }
                t={t}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-600">
              {t("summary")}
            </label>
            <p className="text-gray-900 mt-1 leading-relaxed">
              {bailRight.prosecutor_summary}
            </p>
          </div>

          {bailRight?.prosecutor_document && (
            <FileDownload
              name={t("prosecutor_document")}
              url={bailRight.prosecutor_document.toString()}
              t={t}
            />
          )}
        </div>
      </InfoCard>
    );
  };

  // Render Action Sections based on user role and bail right status
  const renderActionSections = () => {
    if (showFeedback) {
      return (
        <InfoCard title={t("coordinator_feedback")}>
          <BailRightFeedbackSection
            coordinator_feedbacks={bailRight?.coordinator_feedbacks || []}
          />
        </InfoCard>
      );
    }

    return (
      <div className="space-y-6">
        {/* Coordinator Action - Assign Prosecutor */}
        {isCoordinator &&
          bailRight?.status === "forwarded_to_coordinator" &&
          !bailRight?.prosecutor_document &&
          !bailRight?.reference_number && (
            <InfoCard title={t("coordinator_action")}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">
                    {t("assign_prosecutor")}
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                    value={assignProsecutorUserid || ""}
                    onChange={(e) =>
                      setAssignProsecutorUserid(e.target.value || null)
                    }
                  >
                    <option value="">{t("select_prosecutor")}</option>
                    {prosecutors.map((p, index) => (
                      <option key={index} value={p.user?.id || ""}>
                        {p.user?.first_name} {p.user?.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <ActionButton
                  onClick={() => {
                    if (!assignProsecutorUserid) {
                      showTooltip(t("please_select_prosecutor"), "error");
                      return;
                    }
                    setActionIsLoading(true);
                    const data = prosecutors.find(
                      (p) => p.user?.id === assignProsecutorUserid,
                    );

                    editBailRight2({
                      uu_id: bailRight?.uu_id,
                      assign_prosecutor_userid: assignProsecutorUserid,
                      assign_prosecutor: data?.user
                        ? `${data.user.first_name} ${data.user.last_name}`
                        : "",
                      status: "assigned_to_prosecutor",
                    })
                      .then(() => {
                        showTooltip(t("successfully_assigned"), "success");
                        fetchData();
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error),
                          "error",
                        );
                      })
                      .finally(() => setActionIsLoading(false));
                  }}
                  loading={actionIsLoading}
                  t={t}
                >
                  <span>👨‍⚖️</span>
                  {t("assign_to_prosecutor")}
                </ActionButton>
              </div>
            </InfoCard>
          )}

        {/* Prosecutor Action - Submit Summary */}
        {isProsecutor &&
          !bailRight?.reference_number &&
          (bailRight?.status === "assigned_to_prosecutor" ||
            bailRight?.status === "bail_rejected") && (
            <InfoCard title={t("prosecutor_action")}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">
                    {t("bail_evidence_description")}
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={4}
                    value={prosecutorSummary}
                    onChange={(e) => setProsecutorSummary(e.target.value)}
                    placeholder={t("enter_bail_evidence_description")}
                  />
                </div>
                <FileUpload
                  label={t("prosecutor_document")}
                  fileTypes="*"
                  required={true}
                  onChange={(file) => setProsecutorDocument(file)}
                />
                <ActionButton
                  onClick={() => {
                    if (!prosecutorSummary) {
                      showTooltip(
                        t("please_enter_prosecutor_summary"),
                        "error",
                      );
                      return;
                    }
                    setActionIsLoading(true);
                    editBailRight({
                      uu_id: bailRight?.uu_id,
                      prosecutor_summary: prosecutorSummary,
                      prosecutor_document: prosecutorDcument,
                      status: "forwarded_to_coordinator",
                    })
                      .then(() => {
                        showTooltip(t("successfully_submitted"), "success");
                        fetchData();
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error),
                          "error",
                        );
                      })
                      .finally(() => setActionIsLoading(false));
                  }}
                  loading={actionIsLoading}
                  t={t}
                >
                  <span>📤</span>
                  {t("forward_to_coordinator")}
                </ActionButton>
              </div>
            </InfoCard>
          )}

        {/* Add other action sections following the same pattern... */}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Toggle */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">⚖️</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {t("bail_right_case")}
                </h1>
                <p className="text-gray-600">
                  {t("case_reference")}: #{bailRight?.crime_registration_number}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowHearing(!showHearing)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  showHearing
                    ? "bg-red-600 text-white"
                    : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {showHearing
                  ? `📋 ${t("show_case_details")}`
                  : `📅 ${t("show_hearing_decisions")}`}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div
            className={`${
              showFeedback ? "lg:col-span-2" : "lg:col-span-3"
            } space-y-6`}
          >
            {showHearing ? (
              renderHearingDecisions()
            ) : (
              <>
                {renderProsecutorSummary()}
                {renderBailRightInfo()}
              </>
            )}
          </div>

          {/* Right Column - Actions */}
          {showFeedback && (
            <div className="space-y-6">{renderActionSections()}</div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={
            bailRight?.suspect_info?.picture_file ||
            require("../../images/imageVector.png")
          }
          label={`${bailRight?.suspect_info?.first_name} ${bailRight?.suspect_info?.last_name}`}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}

      {isSuspectDetailOpen && (
        <SuspectDetailInfo
          suspect={bailRight?.suspect_info as Suspect}
          isOpen={isSuspectDetailOpen}
          onClose={() => setIsSuspectDetailOpen(false)}
        />
      )}
    </div>
  );
};

export default BailRightDetailOnlyView;
